import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from './firebase';
import './EditStudent.css';

function EditStudent({ student, onStudentUpdated, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showStartDateWarning, setShowStartDateWarning] = useState(false);
  const [showSchoolWarning, setShowSchoolWarning] = useState(false);
  const [showPaymentMethodWarning, setShowPaymentMethodWarning] = useState(false);
  const [showFeeWarning, setShowFeeWarning] = useState(false);
  
  // Normalize t-shirt size to handle different database formats
  const normalizeTShirtSize = (size) => {
    if (!size) return '';
    
    // Convert to lowercase and remove extra spaces
    let normalized = size.toString().toLowerCase().trim();
    
    // Remove " years" or " year" suffix
    normalized = normalized.replace(/\s*years?\s*$/i, '');
    
    // Remove "adult" suffix but keep S, M, L, XL as uppercase
    if (normalized.includes('adult')) {
      normalized = normalized.replace(/\s*\(?\s*adult\s*\)?\s*/i, '').toUpperCase();
    }
    
    return normalized;
  };
  
  const [formData, setFormData] = useState({
    studentName: student.studentName || '',
    dateOfBirth: student.dateOfBirth || '',
    class: student.class || '',
    startDate: student.startDate?.toDate ? 
      student.startDate.toDate().toISOString().split('T')[0] : 
      (student.startDate ? new Date(student.startDate).toISOString().split('T')[0] : ''),
    tShirtSize: normalizeTShirtSize(student.tShirtSize),
    photoPermission: student.photoPermission || 'no',
    paymentMethod: student.paymentMethod || 'term',
    standardFee: student.feeStructure?.standardFee || 0,
    
    // Primary Contact
    primaryContactName: student.primaryContact?.name || '',
    primaryContactId: student.primaryContact?.idNumber || '',
    primaryContactCellPhone: student.primaryContact?.cellPhone || '',
    primaryContactWorkPhone: student.primaryContact?.workPhone || '',
    primaryContactHomePhone: student.primaryContact?.homePhone || '',
    primaryContactEmail: student.primaryContact?.email || '',
    primaryContactRelationship: student.primaryContact?.relationship || '',
    
    // Secondary Contact
    secondaryContactName: student.secondaryContact?.name || '',
    secondaryContactCellPhone: student.secondaryContact?.cellPhone || '',
    secondaryContactWorkPhone: student.secondaryContact?.workPhone || '',
    secondaryContactHomePhone: student.secondaryContact?.homePhone || '',
    secondaryContactEmail: student.secondaryContact?.email || '',
    secondaryContactRelationship: student.secondaryContact?.relationship || '',
  });

  const [originalStartDate, setOriginalStartDate] = useState(formData.startDate);
  const [originalSchoolId, setOriginalSchoolId] = useState(student.schoolRef?.id || '');
  const [originalPaymentMethod, setOriginalPaymentMethod] = useState(student.paymentMethod || 'term');
  const [originalStandardFee, setOriginalStandardFee] = useState(student.feeStructure?.standardFee || 0);

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      const q = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const schoolsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSchools(schoolsList);
      
      // Set current school as selected
      const currentSchool = schoolsList.find(s => s.id === student.schoolRef?.id);
      if (currentSchool) {
        setSelectedSchool(currentSchool);
      }
      
      setLoadingSchools(false);
    } catch (error) {
      console.error('Error loading schools:', error);
      setLoadingSchools(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Check for critical field changes
    if (name === 'startDate' && value !== originalStartDate) {
      setShowStartDateWarning(true);
    } else if (name === 'startDate') {
      setShowStartDateWarning(false);
    }
    
    if (name === 'paymentMethod' && value !== originalPaymentMethod) {
      setShowPaymentMethodWarning(true);
    } else if (name === 'paymentMethod') {
      setShowPaymentMethodWarning(false);
    }
    
    if (name === 'standardFee' && parseFloat(value) !== originalStandardFee) {
      setShowFeeWarning(true);
    } else if (name === 'standardFee') {
      setShowFeeWarning(false);
    }
  };

  const handleSchoolChange = (e) => {
    const schoolId = e.target.value;
    const school = schools.find(s => s.id === schoolId);
    setSelectedSchool(school);
    
    if (schoolId !== originalSchoolId) {
      setShowSchoolWarning(true);
    } else {
      setShowSchoolWarning(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.studentName.trim()) {
      alert('Student name is required');
      return;
    }

    if (!selectedSchool) {
      alert('Please select a school');
      return;
    }

    // Confirm critical changes
    if (showStartDateWarning || showSchoolWarning || showPaymentMethodWarning || showFeeWarning) {
      const warnings = [];
      if (showStartDateWarning) {
        warnings.push('• Start date has changed - this will affect fee calculations');
      }
      if (showSchoolWarning) {
        warnings.push('• School has changed - this will affect the fee structure');
      }
      if (showPaymentMethodWarning) {
        warnings.push('• Payment method has changed - this affects invoice generation');
      }
      if (showFeeWarning) {
        warnings.push('• Term fee has changed - this will affect all billing calculations');
      }

      const confirmed = window.confirm(
        'IMPORTANT CHANGES DETECTED:\n\n' +
        warnings.join('\n') +
        '\n\nAre you sure you want to save these changes?'
      );

      if (!confirmed) {
        return;
      }
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      const studentRef = doc(db, 'students', student.id);

      // Prepare update data
      const updateData = {
        studentName: formData.studentName.trim(),
        dateOfBirth: formData.dateOfBirth,
        class: formData.class,
        startDate: new Date(formData.startDate),
        tShirtSize: formData.tShirtSize,
        photoPermission: formData.photoPermission,
        paymentMethod: formData.paymentMethod,
        
        schoolRef: doc(db, 'schools', selectedSchool.id),
        schoolName: selectedSchool.schoolName,
        
        // Update fee structure
        feeStructure: {
          standardFee: parseFloat(formData.standardFee),
          lessonsPerTerm: student.feeStructure?.lessonsPerTerm || selectedSchool.lessonsPerTerm || 10,
          paymentMethod: formData.paymentMethod
        },
        
        primaryContact: {
          name: formData.primaryContactName,
          idNumber: formData.primaryContactId,
          cellPhone: formData.primaryContactCellPhone,
          workPhone: formData.primaryContactWorkPhone,
          homePhone: formData.primaryContactHomePhone,
          email: formData.primaryContactEmail,
          relationship: formData.primaryContactRelationship
        },
        
        secondaryContact: {
          name: formData.secondaryContactName,
          cellPhone: formData.secondaryContactCellPhone,
          workPhone: formData.secondaryContactWorkPhone,
          homePhone: formData.secondaryContactHomePhone,
          email: formData.secondaryContactEmail,
          relationship: formData.secondaryContactRelationship
        },
        
        updatedAt: new Date(),
        updatedBy: user.email
      };

      // Track changes in edit history
      const changes = [];
      if (formData.startDate !== originalStartDate) {
        changes.push({
          field: 'startDate',
          oldValue: originalStartDate,
          newValue: formData.startDate,
          changedBy: user.email,
          changedAt: new Date()
        });
      }
      if (selectedSchool.id !== originalSchoolId) {
        const oldSchool = schools.find(s => s.id === originalSchoolId);
        changes.push({
          field: 'school',
          oldValue: oldSchool?.schoolName || 'Unknown',
          newValue: selectedSchool.schoolName,
          changedBy: user.email,
          changedAt: new Date()
        });
      }
      if (formData.paymentMethod !== originalPaymentMethod) {
        changes.push({
          field: 'paymentMethod',
          oldValue: originalPaymentMethod,
          newValue: formData.paymentMethod,
          changedBy: user.email,
          changedAt: new Date()
        });
      }
      if (parseFloat(formData.standardFee) !== originalStandardFee) {
        changes.push({
          field: 'standardFee',
          oldValue: `R${originalStandardFee}`,
          newValue: `R${parseFloat(formData.standardFee)}`,
          changedBy: user.email,
          changedAt: new Date()
        });
      }

      if (changes.length > 0) {
        updateData.editHistory = student.editHistory 
          ? [...student.editHistory, ...changes]
          : changes;
      }

      await updateDoc(studentRef, updateData);

      alert(`${formData.studentName} has been successfully updated!`);
      onStudentUpdated();
    } catch (error) {
      console.error('Error updating student:', error);
      alert('Error updating student. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingSchools) {
    return (
      <div className="modal-overlay">
        <div className="edit-student-modal">
          <div className="loading-state">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="edit-student-modal">
        <div className="modal-header">
          <h2>✏️ Edit Student</h2>
          <button 
            onClick={onCancel} 
            className="close-btn"
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-student-form">
          {/* Account Info - Read Only Display */}
          <div className="form-section">
            <h3>Account Information (Read Only)</h3>
            <div className="readonly-info">
              <div className="info-item">
                <span className="label">Account Number:</span>
                <span className="value">{student.accountNumber || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">Created:</span>
                <span className="value">
                  {student.createdAt?.toDate 
                    ? student.createdAt.toDate().toLocaleDateString() 
                    : 'N/A'}
                </span>
              </div>
              <div className="info-item">
                <span className="label">Source:</span>
                <span className="value">{student.source || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">Status:</span>
                <span className={`status-badge ${student.isActive ? 'active' : 'inactive'}`}>
                  {student.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label>Student Name *</label>
              <input
                type="text"
                name="studentName"
                value={formData.studentName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Class</label>
                <input
                  type="text"
                  name="class"
                  value={formData.class}
                  onChange={handleChange}
                  placeholder="e.g., Grade 5"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>T-Shirt Size</label>
                <select
                  name="tShirtSize"
                  value={formData.tShirtSize}
                  onChange={handleChange}
                >
                  <option value="">Select Size</option>
                  <option value="2-3">2-3 years</option>
                  <option value="4-5">4-5 years</option>
                  <option value="5-6">5-6 years</option>
                  <option value="7-8">7-8 years</option>
                  <option value="9-10">9-10 years</option>
                  <option value="11-12">11-12 years</option>
                  <option value="13-14">13-14 years</option>
                  <option value="S">Small (Adult)</option>
                  <option value="M">Medium (Adult)</option>
                  <option value="L">Large (Adult)</option>
                  <option value="XL">XL (Adult)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Photo Permission</label>
                <select
                  name="photoPermission"
                  value={formData.photoPermission}
                  onChange={handleChange}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </div>

          {/* Critical Fields Section */}
          <div className="form-section critical-section">
            <h3>⚠️ Critical Fields</h3>
            <p className="section-note">Changing these fields will affect fee calculations and billing</p>
            
            <div className="form-group">
              <label>School *</label>
              <select
                value={selectedSchool?.id || ''}
                onChange={handleSchoolChange}
                required
              >
                <option value="">Select School</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>
                    {school.schoolName}
                  </option>
                ))}
              </select>
              {showSchoolWarning && (
                <div className="warning-message">
                  ⚠️ Changing school will affect the fee structure
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                />
                {showStartDateWarning && (
                  <div className="warning-message">
                    ⚠️ Changing start date will recalculate all term fees
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                >
                  <option value="term">Pay Per Term</option>
                  <option value="instalment">Monthly Instalments</option>
                </select>
                {showPaymentMethodWarning && (
                  <div className="warning-message">
                    ⚠️ This affects invoice generation
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label>Term Fee Amount (R) *</label>
              <input
                type="number"
                name="standardFee"
                value={formData.standardFee}
                onChange={handleChange}
                step="0.01"
                min="0"
                required
              />
              {showFeeWarning && (
                <div className="warning-message">
                  ⚠️ Changing the fee will affect all billing calculations for this student
                </div>
              )}
              <small>
                Current school fee: R{selectedSchool?.schoolFee?.termFee || 0} per term. 
                This student's fee: R{formData.standardFee}
              </small>
            </div>
          </div>

          {/* Primary Contact */}
          <div className="form-section">
            <h3>Primary Contact</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  name="primaryContactName"
                  value={formData.primaryContactName}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>ID Number</label>
                <input
                  type="text"
                  name="primaryContactId"
                  value={formData.primaryContactId}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Cell Phone</label>
                <input
                  type="tel"
                  name="primaryContactCellPhone"
                  value={formData.primaryContactCellPhone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Work Phone</label>
                <input
                  type="tel"
                  name="primaryContactWorkPhone"
                  value={formData.primaryContactWorkPhone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Home Phone</label>
                <input
                  type="tel"
                  name="primaryContactHomePhone"
                  value={formData.primaryContactHomePhone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="primaryContactEmail"
                  value={formData.primaryContactEmail}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Relationship to Student</label>
              <input
                type="text"
                name="primaryContactRelationship"
                value={formData.primaryContactRelationship}
                onChange={handleChange}
                placeholder="e.g., Mother, Father, Guardian"
              />
            </div>
          </div>

          {/* Secondary Contact */}
          <div className="form-section">
            <h3>Secondary Contact (Optional)</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  name="secondaryContactName"
                  value={formData.secondaryContactName}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Cell Phone</label>
                <input
                  type="tel"
                  name="secondaryContactCellPhone"
                  value={formData.secondaryContactCellPhone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Work Phone</label>
                <input
                  type="tel"
                  name="secondaryContactWorkPhone"
                  value={formData.secondaryContactWorkPhone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Home Phone</label>
                <input
                  type="tel"
                  name="secondaryContactHomePhone"
                  value={formData.secondaryContactHomePhone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="secondaryContactEmail"
                  value={formData.secondaryContactEmail}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Relationship to Student</label>
                <input
                  type="text"
                  name="secondaryContactRelationship"
                  value={formData.secondaryContactRelationship}
                  onChange={handleChange}
                  placeholder="e.g., Mother, Father, Guardian"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn-cancel"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditStudent;